import { LEAGUE_GAME_KEYS } from "./constants";
import { formatCoins, formatPl, formatWonPoints, leagueIncludedGames, plTextClass, wonPointsTextClass } from "./leagueFormat";
import type { League, LeagueGameKey, LeaguePlay, LeaguePlayMarketDetails } from "./types";
import { computeSuperSquaresBreakdown } from "./leagueSuperSquares";

export interface PlayOption {
    id: string;
    game: LeagueGameKey;
    label: string;
    sublabel: string;
    multiplier: number;
}

function teamsFor(league: League | null): [string, string] {
    if (league?.fixture) return [league.fixture.homeTeam, league.fixture.awayTeam];
    return ["India", "Australia"];
}

/**
 * Stand-in market list per game. The real version would read live markets from
 * Trader, the Bullion board, Sixes cards and the Squares over grid.
 */
export function playOptionsForGame(game: LeagueGameKey, league: League | null): PlayOption[] {
    const [home, away] = teamsFor(league);
    if (game === "trader") {
        return [
            { id: "trader-home", game, label: `${home} to win`, sublabel: "Match odds", multiplier: 1.8 },
            { id: "trader-away", game, label: `${away} to win`, sublabel: "Match odds", multiplier: 2.15 },
            { id: "trader-runs", game, label: "Over 165.5 total runs", sublabel: "Innings runs", multiplier: 1.95 },
            { id: "trader-top-bat", game, label: `${home} top batter`, sublabel: "Player market", multiplier: 3.4 },
        ];
    }
    if (game === "bullion") {
        return [
            { id: "bullion-arena", game, label: "Public arena · 5 picks", sublabel: "4 of 5 correct", multiplier: 2.4 },
            { id: "bullion-arena-max", game, label: "Public arena · 5 picks", sublabel: "5 of 5 correct", multiplier: 4.5 },
            { id: "bullion-battle", game, label: "Private battle · 5 picks", sublabel: "Beat the room", multiplier: 3.0 },
        ];
    }
    if (game === "sixes") {
        return [
            { id: "sixes-one", game, label: "One card", sublabel: "Complete a row", multiplier: 2.2 },
            { id: "sixes-two", game, label: "Two cards", sublabel: "Complete a row on either", multiplier: 2.8 },
            { id: "sixes-full", game, label: "One card", sublabel: "Full house", multiplier: 6.0 },
        ];
    }
    return [
        { id: "squares-runs", game, label: "Next over · 4 or more runs", sublabel: "Over grid", multiplier: 2.0 },
        { id: "squares-wicket", game, label: "Next over · wicket falls", sublabel: "Over grid", multiplier: 3.5 },
        { id: "squares-dot", game, label: "Next over · under 4 runs", sublabel: "Over grid", multiplier: 1.75 },
    ];
}

export function allPlayOptions(league: League | null): PlayOption[] {
    const games: LeagueGameKey[] = league ? leagueIncludedGames(league) : [...LEAGUE_GAME_KEYS];
    return games.flatMap((game) => playOptionsForGame(game, league));
}

export function potentialReturn(stake: number, multiplier: number): number {
    return Math.round(stake * multiplier);
}

/** Profit if an open Trader play wins (potential return − stake), floored at 0. */
export function potentialProfit(stake: number, multiplier: number): number {
    return Math.max(0, potentialReturn(stake, multiplier) - Math.max(0, Math.round(stake)));
}

/**
 * Sixes / Bullion are points-model games (not stake × multiplier for league WON).
 * Their `multiplier` on the play row is a placeholder (usually 1) and must not drive UI returns.
 * Bullion league points are the pick score plus a place-bonus share; Sixes uses card score.
 */
export function isLeaguePointsModelGame(game: LeagueGameKey): boolean {
    return game === "sixes" || game === "bullion";
}

type SettledPlayAmount = Pick<LeaguePlay, "game" | "stake" | "delta" | "status" | "outcome" | "marketDetails">;

/**
 * Chip return scored on a settled Squares over — the stored Super Squares total
 * (`returnCoins`, else stake + delta). Same source of truth as admin / Trader / Bullion / Sixes.
 * Live ball grading is only for open overs (`leagueSquaresOpenLiveReturn`).
 */
export function leaguePlayChipReturn(
    play: Pick<LeaguePlay, "stake" | "delta"> &
        Partial<Pick<LeaguePlay, "outcome" | "marketDetails" | "game">>,
): number {
    const outcome = play.outcome != null ? String(play.outcome).toLowerCase() : "";
    if (outcome === "void") return 0;

    const md = play.marketDetails;
    const superReturn = md?.superSquares?.returnCoins;
    if (superReturn != null && Number.isFinite(Number(superReturn))) {
        return Math.max(0, Math.round(Number(superReturn)));
    }

    if (play.delta == null || !Number.isFinite(Number(play.delta))) return 0;
    const stake = Math.max(0, Math.round(Number(play.stake) || 0));
    return Math.max(0, stake + Math.round(Number(play.delta)));
}

/**
 * Live Super Squares chip return for an open play (selections + ball results, or persisted breakdown).
 * Null when there is nothing to score yet.
 */
export function leagueSquaresOpenLiveReturn(
    play: Pick<LeaguePlay, "game" | "status" | "marketDetails">,
): number | null {
    if (play.game !== "squares" || play.status !== "open") return null;
    const md = play.marketDetails;
    if (!md?.selections) return null;

    const candidates: number[] = [];
    const balls = md.ballResults;
    if (Array.isArray(balls) && balls.length > 0) {
        candidates.push(Math.max(0, computeSuperSquaresBreakdown(md.selections, balls).returnCoins));
    }

    const fromSuper = md.superSquares?.returnCoins;
    if (fromSuper != null && Number.isFinite(Number(fromSuper))) {
        candidates.push(Math.max(0, Math.round(Number(fromSuper))));
    }

    if (candidates.length === 0) return null;
    return Math.max(...candidates);
}

/**
 * Trader league points: full return (stake × odds), never negative.
 * A loss scores 0; a win scores stake + profit (not profit-only / free-bet style).
 * Void releases the stake hold only — never awards PTS (delta is 0, which would otherwise
 * look like stake + 0 = stake).
 */
export function leagueTraderWonPoints(
    play: Pick<LeaguePlay, "stake" | "delta"> & Partial<Pick<LeaguePlay, "outcome">>,
): number {
    const outcome = play.outcome != null ? String(play.outcome).toLowerCase() : "";
    if (outcome === "void") return 0;
    const stake = Math.max(0, Math.round(Number(play.stake) || 0));
    const delta = Math.round(Number(play.delta) || 0);
    return Math.max(0, stake + delta);
}

/**
 * PTS shown on My Trades cards — open = potential return if it wins; settled = won return.
 * Must match leaderboard Trader scoring (stake × odds, not profit-only).
 */
export function leagueTraderPlayPoints(
    play: Pick<LeaguePlay, "status" | "stake" | "multiplier" | "delta"> &
        Partial<Pick<LeaguePlay, "outcome">>,
): number {
    if (play.status === "open") return potentialReturn(play.stake, play.multiplier);
    return leagueTraderWonPoints(play);
}

/**
 * Bullion / Sixes settled Activity points — never coin P/L.
 * Bullion uses slip + place bonus stored on `settlement.points` after battle settle.
 */
export function leaguePointsModelSettledPoints(play: SettledPlayAmount): number {
    if (play.game === "bullion") {
        const points = Number(play.marketDetails?.settlement?.points);
        if (Number.isFinite(points)) return Math.max(0, Math.round(points));
        return 0;
    }
    if (play.game === "sixes") {
        const score = Number(play.marketDetails?.cardScore);
        if (Number.isFinite(score)) return Math.max(0, Math.round(score));
        // Stake 0 ⇒ delta === card return; never surface a red loss.
        return Math.max(0, Math.round(Number(play.delta) || 0));
    }
    return 0;
}

/**
 * Points shown on settled rows. Squares uses chip return (counts toward league WON),
 * not net P/L — you can score on correct balls even when the over loses overall.
 * Trader uses full return (0 on a loss). Bullion uses slip + place bonus; Sixes uses card score.
 */
export function leaguePlaySettledPoints(play: SettledPlayAmount): number {
    if (play.game === "squares") return leaguePlayChipReturn(play);
    if (play.game === "trader") return leagueTraderWonPoints(play);
    if (isLeaguePointsModelGame(play.game)) return leaguePointsModelSettledPoints(play);
    return Math.round(Number(play.delta) || 0);
}

export function formatLeaguePlaySettledPoints(play: SettledPlayAmount): string {
    if (play.game === "squares") return formatWonPoints(leaguePlayChipReturn(play));
    if (play.game === "trader") return formatWonPoints(leagueTraderWonPoints(play));
    if (isLeaguePointsModelGame(play.game)) return formatWonPoints(leaguePointsModelSettledPoints(play));
    return formatPl(play.delta ?? 0);
}

export function leaguePlaySettledPointsClass(play: SettledPlayAmount): string {
    if (play.game === "squares") return wonPointsTextClass(leaguePlayChipReturn(play));
    if (play.game === "trader") return wonPointsTextClass(leagueTraderWonPoints(play));
    if (isLeaguePointsModelGame(play.game)) {
        return wonPointsTextClass(leaguePointsModelSettledPoints(play));
    }
    return plTextClass(play.delta ?? 0);
}

/** Short status under settled Squares rows — avoid "Lost" when the over still scored league points. */
export function leagueSquaresSettledStatusLabel(play: Pick<LeaguePlay, "outcome" | "stake" | "delta">): string {
    const scored = leaguePlayChipReturn(play);
    if (scored <= 0) return "Lost";
    return play.outcome === "win" ? "Won" : "Scored";
}

/** Coin-denominated open-play return — zero for points-model games (Bullion / Sixes). */
export function openPlayCoinPotentialReturn(play: LeaguePlay): number {
    if (isLeaguePointsModelGame(play.game)) return 0;
    return potentialReturn(play.stake, play.multiplier);
}

/**
 * Live WON points for an open Activity row (replaces stake on Active).
 * - Sixes: running card score
 * - Bullion: progressive slip score (same live basis as the battle board; slip + place after settle)
 * - Squares: Super return from ball results / persisted breakdown (same as leaderboard)
 * - Trader: null until the play settles (shown on Past)
 */
export function leaguePlayOpenLivePoints(play: Pick<LeaguePlay, "game" | "status" | "marketDetails">): number | null {
    if (play.status !== "open") return null;

    if (play.game === "sixes") {
        const score = Number(play.marketDetails?.cardScore);
        if (!Number.isFinite(score)) return null;
        return Math.max(0, Math.round(score));
    }

    if (play.game === "bullion") {
        const settlement = play.marketDetails?.settlement;
        if (!settlement) return null;
        const slip =
            settlement.slipScore != null && Number.isFinite(Number(settlement.slipScore))
                ? Number(settlement.slipScore)
                : Number(settlement.points);
        if (!Number.isFinite(slip)) return null;
        return Math.max(0, Math.round(slip));
    }

    if (play.game === "squares") return leagueSquaresOpenLiveReturn(play);

    return null;
}

export function formatLeaguePlayOpenLivePoints(
    play: Pick<LeaguePlay, "game" | "status" | "marketDetails">,
): string | null {
    const pts = leaguePlayOpenLivePoints(play);
    if (pts == null) return null;
    return formatWonPoints(pts);
}

/**
 * Subtitle under the open-play stake chip.
 * Bullion ranks live on slip score; table points are that score plus a place-bonus share. Sixes has no fixed return.
 */
export function openPlayReturnLabel(play: LeaguePlay): string {
    if (play.game === "sixes") return "returns card total";
    if (play.game === "bullion") return "live pick score";
    return `to return ${formatCoins(potentialReturn(play.stake, play.multiplier))}`;
}

export function formatMultiplier(multiplier: number): string {
    return `${multiplier.toFixed(2)}×`;
}

/** Human market title for a Trader play (e.g. "Match Odds", "Line 165.5"). */
export function leaguePlayMarketLabel(details: LeaguePlayMarketDetails | undefined): string | null {
    if (!details) return null;
    if (details.marketName?.trim()) return details.marketName.trim();
    if (details.marketClass === "Line" && details.marketLine != null && String(details.marketLine).trim() !== "") {
        const line = Number(details.marketLine);
        return Number.isFinite(line) ? `Line ${line.toFixed(1)}` : `Line ${details.marketLine}`;
    }
    if (details.marketClass === "Win") return "Match Winner";
    return null;
}

/** Decimal type ids that are match winner — never infer this from a selection name. */
const TRADER_ACTIVITY_MATCH_WINNER_TYPE_IDS = new Set(["10", "1010", "15"]);
const TRADER_ACTIVITY_TOSS_TYPE_IDS = new Set(["815"]);
const TRADER_ACTIVITY_PLAYER_TYPE_IDS = new Set([
    "270",
    "280",
    "621",
    "710",
    "720",
    "730",
    "760",
    "910",
    "912",
    "914",
    "940",
    "950",
]);
const TRADER_ACTIVITY_SESSION_TYPE_IDS = new Set([
    "340",
    "342",
    "344",
    "405",
    "410",
    "415",
    "455",
    "460",
    "510",
    "516",
    "855",
    "860",
]);
const TRADER_ACTIVITY_INNINGS_TYPE_IDS = new Set([
    "209",
    "210",
    "216",
    "217",
    "220",
    "230",
    "240",
    "250",
    "260",
    "610",
    "630",
    "635",
]);
const TRADER_ACTIVITY_H2H_TYPE_IDS = new Set(["30", "35", "45", "50", "55", "94", "96"]);
const TRADER_ACTIVITY_MATCH_TYPE_IDS = new Set([
    "12",
    "16",
    "25",
    "60",
    "70",
    "72",
    "75",
    "77",
    "80",
    "85",
    "86",
    "88",
    "90",
    "865",
    "870",
]);

/**
 * Coarse market family for public Trader activity.
 * Stake + type only — never the pick, player, team, line, or odds.
 */
export function leagueTraderActivityMarketFamily(
    details: LeaguePlayMarketDetails | null | undefined,
): string {
    const id = String(details?.marketTypeId ?? "")
        .trim()
        .replace(/\.0+$/, "");
    const hay = String(details?.marketName ?? "").toLowerCase();

    if (
        TRADER_ACTIVITY_MATCH_WINNER_TYPE_IDS.has(id) ||
        hay.includes("match winner") ||
        hay.includes("match odds")
    ) {
        return "Match Winner";
    }
    if (TRADER_ACTIVITY_TOSS_TYPE_IDS.has(id) || hay.includes("toss winner") || hay === "toss") {
        return "Toss Winner";
    }
    if (TRADER_ACTIVITY_PLAYER_TYPE_IDS.has(id)) return "Player";
    if (TRADER_ACTIVITY_SESSION_TYPE_IDS.has(id)) return "Session";
    if (TRADER_ACTIVITY_INNINGS_TYPE_IDS.has(id)) return "Innings";
    if (TRADER_ACTIVITY_H2H_TYPE_IDS.has(id)) return "Head to Head";
    if (TRADER_ACTIVITY_MATCH_TYPE_IDS.has(id)) return "Match";

    if (hay.includes("innings")) return "Innings";
    if (hay.includes("session") || hay.includes("runs in over") || hay.includes("first 6")) {
        return "Session";
    }
    if (hay.includes("head to head") || hay.includes("head-to-head") || hay.includes("h2h")) {
        return "Head to Head";
    }
    if (hay.includes("player") || hay.includes("batter") || hay.includes("batsman") || hay.includes("bowler")) {
        return "Player";
    }
    if (details?.marketClass === "Win" && !hay.trim()) return "Match Winner";
    return "Trader";
}

/** Over-only Squares activity label — never ball-by-ball picks. */
export function leagueSquaresOverLabel(
    selectionOrDetail: string,
    details?: LeaguePlayMarketDetails,
): string {
    const over = details?.overNumber;
    if (over != null && Number.isFinite(Number(over)) && Number(over) >= 1) {
        return `Over ${Math.floor(Number(over))}`;
    }
    const fromText = String(selectionOrDetail || "").match(/\bOver\s+(\d+)\b/i);
    if (fromText) return `Over ${fromText[1]}`;
    if (/next\s+over/i.test(String(selectionOrDetail || ""))) return "Next over";
    return "Squares+ over";
}

/** Compact ball-pick list for fallback text, e.g. "B1 4, B2 6". */
export function leagueSquaresBallPicksLabel(
    selections?: LeaguePlayMarketDetails["selections"],
): string | null {
    if (!selections || typeof selections !== "object") return null;
    const parts = Object.entries(selections)
        .filter(([, sel]) => sel != null && sel.run != null && String(sel.run).trim() !== "")
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([ball, sel]) => `B${ball} ${String(sel!.run).trim()}`);
    return parts.length > 0 ? parts.join(", ") : null;
}

/** Unit suffix for Over/Under line picks — derived from market name / type id. */
function leagueTraderLineUnit(details: LeaguePlayMarketDetails): string {
    const id = String(details.marketTypeId ?? "").trim();
    if (id === "914" || id === "730") return "sixes";
    if (id === "912" || id === "720") return "fours";
    if (id === "910" || id === "710") return "runs";

    const hay = String(details.marketName ?? "").toLowerCase();
    if (hay.includes("fall of wicket")) return "runs";
    if (/\bsixes\b/.test(hay)) return "sixes";
    if (/\bfours\b/.test(hay)) return "fours";
    if (/\bwickets?\b/.test(hay)) return "wickets";
    if (/\bextras\b/.test(hay)) return "extras";
    if (/\brun[\s-]?outs?\b/.test(hay)) return "run outs";
    if (/\bruns?\b/.test(hay)) return "runs";
    return "runs";
}

function lineTokenInLabel(label: string, lineLabel: string, lineNum: number): boolean {
    const escaped = lineLabel.replace(/\./g, "\\.");
    if (new RegExp(`\\b${escaped}\\b`).test(label)) return true;
    if (Number.isFinite(lineNum)) {
        const numEscaped = String(lineNum).replace(/\./g, "\\.");
        if (new RegExp(`\\b${numEscaped}\\b`).test(label)) return true;
    }
    return false;
}

/**
 * Prefer a structured pick label when market details are present.
 * Line Over/Under → e.g. "Under 62.5 runs" (line + unit), keeping odds/settlement suffixes.
 */
export function leaguePlayPickLabel(selectionOrDetail: string, details: LeaguePlayMarketDetails | undefined): string {
    if (!details?.selectionName?.trim()) return selectionOrDetail;
    let pick = details.selectionName
        .trim()
        .replace(/\bOVER\b/g, "Over")
        .replace(/\bUNDER\b/g, "Under");
    const isOverUnder = /^(over|under)\b/i.test(pick);
    const hasLine =
        details.marketLine != null &&
        String(details.marketLine).trim() !== "" &&
        (details.marketClass === "Line" || isOverUnder);

    if (hasLine) {
        const line = Number(details.marketLine);
        const lineLabel = Number.isFinite(line) ? line.toFixed(1) : String(details.marketLine).trim();
        if (!lineTokenInLabel(pick, lineLabel, line)) {
            pick = `${pick} ${lineLabel}`;
        }
        if (isOverUnder) {
            const unit = leagueTraderLineUnit(details);
            const unitPattern = unit.replace(/\s+/g, "\\s+");
            if (!new RegExp(`\\b${unitPattern}\\b`, "i").test(pick)) {
                pick = `${pick} ${unit}`;
            }
        }
    }
    // Preserve trailing odds / settlement suffix from the stored string when present.
    const atIdx = selectionOrDetail.lastIndexOf(" @ ");
    const dotIdx = selectionOrDetail.indexOf(" · ");
    if (atIdx >= 0 && (dotIdx < 0 || atIdx < dotIdx)) {
        const oddsAndRest = selectionOrDetail.slice(atIdx); // " @ 1.85" or " @ 1.85 · lost"
        // Avoid duplicating the selection name if the stored string already starts with it.
        if (!selectionOrDetail.toLowerCase().startsWith(pick.toLowerCase())) {
            return `${pick}${oddsAndRest}`;
        }
    }
    if (dotIdx > 0 && !selectionOrDetail.toLowerCase().startsWith(pick.toLowerCase())) {
        // Settled detail like "India @ 1.85 · returned 740" — rebuild head, keep outcome tail.
        const tail = selectionOrDetail.slice(dotIdx); // " · returned 740"
        const head = atIdx >= 0 ? `${pick}${selectionOrDetail.slice(atIdx, dotIdx)}` : pick;
        return `${head}${tail}`;
    }
    if (!selectionOrDetail.toLowerCase().startsWith(pick.toLowerCase())) {
        return pick;
    }
    return selectionOrDetail;
}

/**
 * Activity subtitle lines: optional market header + pick/detail body.
 * Squares always returns the over label only — pick cells render separately.
 */
export function leagueActivityDetailLines(input: {
    selectionOrDetail: string;
    marketDetails?: LeaguePlayMarketDetails;
    game?: LeagueGameKey;
}): { market: string | null; detail: string } {
    if (input.game === "squares") {
        return {
            market: null,
            detail: leagueSquaresOverLabel(input.selectionOrDetail, input.marketDetails),
        };
    }
    const market = leaguePlayMarketLabel(input.marketDetails);
    const detail = leaguePlayPickLabel(input.selectionOrDetail, input.marketDetails);
    if (!market) return { market: null, detail };
    // Newer settlement rows already prefix the market name — don't duplicate.
    if (detail.toLowerCase().startsWith(market.toLowerCase())) {
        return { market: null, detail };
    }
    return { market, detail };
}

export function isOpenPlay(play: LeaguePlay): boolean {
    return play.status === "open";
}

export function openPlaysFor(league: League, userId?: number): LeaguePlay[] {
    return league.plays.filter(
        (play) => play.status === "open" && (userId == null || play.userId === userId),
    );
}

export function openStakeFor(league: League, userId: number): number {
    return openPlaysFor(league, userId).reduce((total, play) => total + play.stake, 0);
}

/** Settled balance minus anything already committed to open plays. */
export function availableCoinsFor(league: League, userId: number): number {
    const member =
        league.members.find((row) => row.userId === userId) ??
        league.members.find((row) => row.isViewer);
    if (!member) return 0;
    return Math.max(0, member.coins - openStakeFor(league, member.userId));
}

/** Soft ETAs (e.g. league ends_at) can expire while the match is still live. */
const SETTLING_HINT_MS = 30_000;

export function settlesInMs(play: LeaguePlay, now: number = Date.now()): number | null {
    if (!play.settlesAt) return null;
    const settles = new Date(play.settlesAt).getTime();
    if (!Number.isFinite(settles)) return null;
    return settles - now;
}

/** Compact settle countdown for lobby / in-play rows. */
export function openPlayCountdownLabel(play: LeaguePlay, now: number = Date.now()): string {
    const ms = settlesInMs(play, now);
    if (ms == null) return "in play";
    if (ms <= 0) {
        // Real settlement is event-driven. Only hint "settling…" for a short overdue
        // window (demo / short horizons); long-overdue soft ETAs stay "in play".
        return ms > -SETTLING_HINT_MS ? "settling…" : "in play";
    }
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `settles in ${seconds}s`;
    return `settles in ${Math.ceil(seconds / 60)}m`;
}

/**
 * 0–1 fill for the open-play progress bar from placedAt → settlesAt.
 * Returns null when there is no usable ETA (caller should show indeterminate UI).
 */
export function openPlayProgress(play: LeaguePlay, now: number = Date.now()): number | null {
    if (!play.settlesAt) return null;
    const placed = new Date(play.placedAt).getTime();
    const settles = new Date(play.settlesAt).getTime();
    if (!Number.isFinite(placed) || !Number.isFinite(settles) || settles <= placed) return null;
    if (now >= settles) {
        // Brief overdue: full bar. Long overdue soft ETA: indeterminate.
        return now - settles <= SETTLING_HINT_MS ? 1 : null;
    }
    return Math.min(1, Math.max(0, (now - placed) / (settles - placed)));
}
